/**
 * Onboarding.tsx — Single-add + bulk CSV account onboarding (D-04/D-05).
 *
 * Security (T-04-31):
 *   - Password is submitted once via POST body; NEVER rendered back
 *   - ValidationTable shows status only — no credentials
 *   - Form clears on submit to prevent accidental exposure
 */

import * as React from 'react';
import type { CreatorLabel } from '@crm/types';
import { CREATOR_LABELS, CREATOR_LABEL_META } from '@crm/types';
import { useMutation } from '@tanstack/react-query';
import { onboardingApi, ApiRequestError } from '../lib/api-client.js';
import type { BulkOnboardingResponse, OnboardingResultRow } from '@crm/types';
import { ValidationTable } from '../components/ValidationTable.js';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useToast,
} from '@crm/ui';

// ---------------------------------------------------------------------------
// Initial row type used by ValidationTable
// ---------------------------------------------------------------------------

type InitialRowWithDisplay = OnboardingResultRow & {
  displayLabel: string;
  email: string;
  rowId?: string;
};

// ---------------------------------------------------------------------------
// Single Add form
// ---------------------------------------------------------------------------

function SingleAddForm() {
  const { addToast } = useToast();

  const [form, setForm] = React.useState({
    email: '',
    password: '',
    displayLabel: '',
    creatorName: '',
    notes: '',
  });

  const [result, setResult] = React.useState<BulkOnboardingResponse | null>(null);
  const [initialRows, setInitialRows] = React.useState<InitialRowWithDisplay[]>([]);
  // Creator labels (e.g. trans) travel with the row so the Post Bot rule applies from the first post.
  const [labels, setLabels] = React.useState<CreatorLabel[]>([]);

  const setField =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const savedEmail = React.useRef('');
  const savedLabel = React.useRef('');

  const mut = useMutation({
    mutationFn: () => {
      // Capture before clearing
      savedEmail.current = form.email;
      savedLabel.current = form.displayLabel;
      return onboardingApi.addSingle({
        email: form.email,
        password: form.password,
        displayLabel: form.displayLabel,
        creatorName: form.creatorName,
        notes: form.notes || undefined,
        labels: labels.length ? labels : undefined,
      });
    },
    onSuccess: (res) => {
      setResult(res);
      setInitialRows(
        res.rows.map((r): InitialRowWithDisplay => ({
          ...r,
          displayLabel: savedLabel.current,
          email: savedEmail.current,
        })),
      );
      // Clear form immediately — T-04-31: no password persists in state
      setForm({
        email: '',
        password: '',
        displayLabel: '',
        creatorName: '',
        notes: '',
      });
      setLabels([]);
      addToast(`Queued ${res.accepted} account for validation.`, 'success');
    },
    onError: (err) => {
      const msg =
        err instanceof ApiRequestError ? err.message : 'Onboarding failed';
      addToast(msg, 'danger');
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setResult(null);
          mut.mutate();
        }}
        className="grid grid-cols-2 gap-3"
        autoComplete="off"
      >
        <Input
          label="Email *"
          type="email"
          required
          value={form.email}
          onChange={setField('email')}
          disabled={mut.isPending}
          autoComplete="off"
        />
        {/* T-04-31: type=password; autocomplete=new-password prevents browser fill */}
        <Input
          label="Password *"
          type="password"
          required
          value={form.password}
          onChange={setField('password')}
          disabled={mut.isPending}
          autoComplete="new-password"
          placeholder="Account password (transit only)"
        />
        <Input
          label="Display Label *"
          required
          value={form.displayLabel}
          onChange={setField('displayLabel')}
          disabled={mut.isPending}
          placeholder="e.g. @creator_handle"
        />
        <Input
          label="Creator Name *"
          required
          value={form.creatorName}
          onChange={setField('creatorName')}
          disabled={mut.isPending}
        />
        <fieldset className="col-span-2 flex flex-col gap-1">
          <legend className="text-sm text-text">Creator labels</legend>
          {CREATOR_LABELS.map((label) => (
            <label key={label} className="flex items-start gap-2 text-sm text-text cursor-pointer">
              <input
                type="checkbox"
                className="accent-accent mt-0.5"
                checked={labels.includes(label)}
                disabled={mut.isPending}
                onChange={(e) =>
                  setLabels((prev) =>
                    e.target.checked ? [...prev.filter((l) => l !== label), label] : prev.filter((l) => l !== label),
                  )
                }
              />
              <span>
                <span className="font-medium">{CREATOR_LABEL_META[label].title}</span>{' '}
                <span className="text-xs text-text-muted">— {CREATOR_LABEL_META[label].description}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="col-span-2">
          <Textarea
            label="Internal Notes"
            value={form.notes}
            onChange={setField('notes')}
            disabled={mut.isPending}
            placeholder="Admin notes"
          />
        </div>
        <div className="col-span-2 flex justify-end">
          <Button type="submit" loading={mut.isPending}>
            Add account
          </Button>
        </div>
      </form>

      {result && result.rows.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-text-muted uppercase mb-2">
            Validation status
          </h3>
          <ValidationTable batchId={result.batchId} initialRows={initialRows} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV upload form
// ---------------------------------------------------------------------------

/**
 * Expected CSV columns (D-04; see apps/api csv-parser.ts):
 * email, password, display_label, creator, notes, tags, labels
 */
function CsvUploadForm() {
  const { addToast } = useToast();
  const [file, setFile] = React.useState<File | null>(null);
  const [result, setResult] = React.useState<BulkOnboardingResponse | null>(null);
  const [initialRows, setInitialRows] = React.useState<InitialRowWithDisplay[]>([]);

  const mut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      return onboardingApi.addBulkCsv(file);
    },
    onSuccess: (res) => {
      setResult(res);
      setInitialRows(
        res.rows.map((r): InitialRowWithDisplay => ({
          ...r,
          displayLabel: `Row ${r.rowIndex + 1}`,
          email: '',
        })),
      );
      setFile(null);
      addToast(
        `Bulk: ${res.accepted} accepted, ${res.rejected} rejected.`,
        res.rejected === 0 ? 'success' : 'warning',
      );
    },
    onError: (err) => {
      const msg =
        err instanceof ApiRequestError ? err.message : 'CSV upload failed';
      addToast(msg, 'danger');
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-text-muted">
        CSV columns:{' '}
        <code className="font-mono text-text">
          email, password, display_label, creator, notes, tags, labels
        </code>{' '}
        — <code className="font-mono text-text">labels</code> is optional (e.g. <code className="font-mono text-text">trans</code>).
      </p>

      <div className="flex items-center gap-3">
        <label className="flex flex-col gap-1 flex-1">
          <span className="text-xs font-medium text-text-muted uppercase tracking-wide">
            CSV file
          </span>
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={mut.isPending}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              setResult(null);
            }}
            className="text-sm text-text file:mr-3 file:px-3 file:py-1 file:text-xs file:rounded file:border file:border-border file:bg-bg-card file:text-text hover:file:bg-bg-hover cursor-pointer"
          />
        </label>
        <Button
          onClick={() => mut.mutate()}
          loading={mut.isPending}
          disabled={!file}
          className="mt-4"
        >
          Upload &amp; validate
        </Button>
      </div>

      {result && result.rows.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-text-muted uppercase mb-2">
            Per-row validation status (live)
          </h3>
          <ValidationTable batchId={result.batchId} initialRows={initialRows} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Onboarding() {
  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-text">Onboarding</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Add a single account or upload a bulk CSV. Credential validation runs
          asynchronously; status updates live below.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <Tabs defaultTab="single">
            <TabsList>
              <TabsTrigger value="single">Single account</TabsTrigger>
              <TabsTrigger value="csv">Bulk CSV</TabsTrigger>
            </TabsList>
            <TabsContent value="single">
              <SingleAddForm />
            </TabsContent>
            <TabsContent value="csv">
              <CsvUploadForm />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
